import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { LogoMark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ob_admin_token";

const inputCls =
  "w-full border border-stone-300 bg-transparent px-5 py-4 text-sm text-ink placeholder:text-stone-400 outline-none transition-colors duration-300 focus:border-terra";

const EMPTY = { title: "", category: "", date: new Date().toISOString().slice(0, 10), read_time: "4 min read", excerpt: "", body: "", image: "" };

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [publishing, setPublishing] = useState(false);

  const loadPosts = useCallback(() => {
    axios.get(`${API}/journal`).then((r) => setPosts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (token) loadPosts();
  }, [token, loadPosts]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Welcome back.");
    } catch {
      toast.error("Incorrect password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const publish = async (e) => {
    e.preventDefault();
    if (publishing) return;
    setPublishing(true);
    try {
      const body = form.body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
      await axios.post(
        `${API}/journal`,
        { title: form.title, category: form.category, date: form.date, read_time: form.read_time, excerpt: form.excerpt, body, image: form.image || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Story published.");
      setForm(EMPTY);
      loadPosts();
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired — please sign in again.");
      } else {
        toast.error("Could not publish. Check every field is filled.");
      }
    } finally {
      setPublishing(false);
    }
  };

  const remove = async (slug) => {
    try {
      await axios.delete(`${API}/journal/${slug}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Story deleted.");
      loadPosts();
    } catch {
      toast.error("Could not delete this story.");
    }
  };

  if (!token) {
    return (
      <section data-testid="admin-login" className="flex min-h-screen items-center justify-center bg-ivory px-6 pt-20">
        <Reveal className="w-full max-w-md">
          <form onSubmit={login} className="border border-stone-200 bg-stone-100/60 p-10 md:p-14">
            <LogoMark stroke="#1C1917" className="h-10 w-10" />
            <h1 className="mt-6 font-serif text-3xl text-ink">Journal Studio</h1>
            <p className="mt-2 text-sm text-stone-500">Sign in to publish new stories.</p>
            <input
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              required
              className={`${inputCls} mt-8`}
            />
            <button
              type="submit"
              data-testid="admin-login-button"
              disabled={loggingIn}
              className="group mt-6 inline-flex w-full items-center justify-center gap-3 bg-ink px-8 py-4 font-label text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-terra disabled:opacity-60"
            >
              {loggingIn ? "Signing in" : "Sign In"}
              {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
            </button>
          </form>
        </Reveal>
      </section>
    );
  }

  return (
    <section data-testid="admin-studio" className="bg-ivory pb-28 pt-36 md:pt-44">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.3em] text-stone-500">Journal Studio</p>
            <h1 className="mt-4 font-serif text-4xl tracking-tight text-ink md:text-5xl">Publish a new story.</h1>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); }}
            className="link-line font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500 hover:text-terra"
          >
            Sign out
          </button>
        </div>

        <div className="mt-14 grid gap-14 lg:grid-cols-12">
          <form onSubmit={publish} data-testid="admin-publish-form" className="space-y-7 border border-stone-200 bg-stone-100/60 p-8 md:p-10 lg:col-span-7">
            <input data-testid="post-title" required value={form.title} onChange={set("title")} placeholder="Story title" className={inputCls} />
            <div className="grid gap-7 sm:grid-cols-3">
              <input data-testid="post-category" required value={form.category} onChange={set("category")} placeholder="Category (e.g. Sourcing)" className={inputCls} />
              <input data-testid="post-date" required type="date" value={form.date} onChange={set("date")} className={inputCls} />
              <input data-testid="post-read-time" required value={form.read_time} onChange={set("read_time")} placeholder="4 min read" className={inputCls} />
            </div>
            <textarea data-testid="post-excerpt" required rows={2} value={form.excerpt} onChange={set("excerpt")} placeholder="Excerpt — one or two sentences shown on the journal list" className={`${inputCls} resize-none`} />
            <input data-testid="post-image" value={form.image} onChange={set("image")} placeholder="Cover image URL (optional — paste an image link)" className={inputCls} />
            <div>
              <textarea data-testid="post-body" required rows={12} value={form.body} onChange={set("body")} placeholder={"Write the story here.\n\nSeparate paragraphs with a blank line."} className={`${inputCls} resize-none`} />
              <p className="mt-2 text-xs text-stone-400">Paragraphs are split by blank lines.</p>
            </div>
            <button
              type="submit"
              data-testid="post-publish-button"
              disabled={publishing}
              className="group inline-flex items-center gap-3 bg-terra px-10 py-5 font-label text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors duration-300 hover:bg-terra-dark disabled:opacity-60"
            >
              {publishing ? "Publishing" : "Publish Story"}
              {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
            </button>
          </form>

          <div className="lg:col-span-5">
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
              Published stories ({posts.length})
            </p>
            <ul className="mt-6 divide-y divide-stone-200 border-y border-stone-200" data-testid="admin-post-list">
              {posts.map((p) => (
                <li key={p.slug} className="flex items-start justify-between gap-4 py-5">
                  <div>
                    <p className="font-label text-[10px] font-semibold uppercase tracking-[0.24em] text-terra">{p.category}</p>
                    <p className="mt-1 font-serif text-lg leading-snug text-ink">{p.title}</p>
                  </div>
                  <button
                    data-testid={`delete-post-${p.slug}`}
                    onClick={() => remove(p.slug)}
                    aria-label={`Delete ${p.title}`}
                    className="mt-1 text-stone-400 transition-colors duration-300 hover:text-terra"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
